import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, Home, Calculator, RotateCcw, Info } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';
import { Tooltip } from '../../../../components/ui/Tooltip';
import { LABEL_STANDARD } from '../../../../lib/formStyles';

const SQRT3 = Math.sqrt(3);

const TABS = [
  { id: '1phase', label: '1 Phase', title: 'kVA Single Phase' },
  { id: '3phase', label: '3 Phase', title: 'kVA Three Phase' },
  { id: 'pf', label: 'kVA | PF | kW', title: 'kVA | PF | kW' },
];

const initialValues = {
  '1phase': { kva: '', volts: '', amps: '' },
  '3phase': { kva: '', volts: '', amps: '' },
  pf: { kva: '', pf: '', kw: '' },
};

function InputRow({ label, placeholder, value, onChange, unitSuffix, infoTooltip }) {
  return (
    <div>
      <label className={LABEL_STANDARD} htmlFor={label}>
        {label}
        {infoTooltip && (
          <Tooltip content={infoTooltip}>
            <button
              type="button"
              className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors"
              aria-label="Information"
            >
              <Info className="h-3 w-3" />
            </button>
          </Tooltip>
        )}
      </label>
      <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
        <input
          id={label}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none placeholder:text-gray-500 dark:placeholder:text-gray-400"
        />
        {unitSuffix && (
          <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-l border-gray-300 dark:border-gray-600">
            {unitSuffix}
          </span>
        )}
      </div>
    </div>
  );
}

export default function KVACalc() {
  const location = useLocation();
  const isMobile = location.pathname.startsWith('/tenant/mobile');

  const [activeTab, setActiveTab] = useState('1phase');
  const [values, setValues] = useState(initialValues);
  const [result, setResult] = useState(null);

  const backPath = isMobile ? '/tenant/mobile/calculators' : '/tenant/tools/calculators';

  const updateValues = (tab, field, val) => {
    setValues((p) => ({
      ...p,
      [tab]: { ...p[tab], [field]: val },
    }));
    setResult(null);
  };

  const countFilled = (arr) => arr.filter((v) => v !== '' && v !== null && v !== undefined).length;

  const handleCalculate = () => {
    if (activeTab === '1phase') {
      const { kva, volts, amps } = values['1phase'];
      const k = parseFloat(kva);
      const v = parseFloat(volts);
      const i = parseFloat(amps);
      const filled = countFilled([kva, volts, amps]);
      if (filled !== 2) return;

      let calc;
      if (!kva || kva === '') {
        calc = (v * i) / 1000;
        setResult({ field: 'kva', value: calc.toFixed(4), unit: 'kVA' });
        updateValues('1phase', 'kva', calc.toFixed(4));
      } else if (!volts || volts === '') {
        calc = (k * 1000) / i;
        setResult({ field: 'volts', value: calc.toFixed(2), unit: 'V' });
        updateValues('1phase', 'volts', calc.toFixed(2));
      } else {
        calc = (k * 1000) / v;
        setResult({ field: 'amps', value: calc.toFixed(2), unit: 'A' });
        updateValues('1phase', 'amps', calc.toFixed(2));
      }
    } else if (activeTab === '3phase') {
      const { kva, volts, amps } = values['3phase'];
      const k = parseFloat(kva);
      const v = parseFloat(volts);
      const i = parseFloat(amps);
      const filled = countFilled([kva, volts, amps]);
      if (filled !== 2) return;

      let calc;
      if (!kva || kva === '') {
        calc = (SQRT3 * v * i) / 1000;
        setResult({ field: 'kva', value: calc.toFixed(4), unit: 'kVA' });
        updateValues('3phase', 'kva', calc.toFixed(4));
      } else if (!volts || volts === '') {
        calc = (k * 1000) / (SQRT3 * i);
        setResult({ field: 'volts', value: calc.toFixed(2), unit: 'V' });
        updateValues('3phase', 'volts', calc.toFixed(2));
      } else {
        calc = (k * 1000) / (SQRT3 * v);
        setResult({ field: 'amps', value: calc.toFixed(2), unit: 'A' });
        updateValues('3phase', 'amps', calc.toFixed(2));
      }
    } else {
      const { kva, pf, kw } = values.pf;
      let k = parseFloat(kva);
      let p = parseFloat(pf);
      const w = parseFloat(kw);
      // PF can be entered as 0.85 or 85 - normalise to 0–1
      if (p > 1) p = p / 100;
      const filled = countFilled([kva, pf, kw]);
      if (filled !== 2) return;

      let calc;
      if (!kva || kva === '') {
        calc = p > 0 ? w / p : 0;
        setResult({ field: 'kva', value: calc.toFixed(4), unit: 'kVA' });
        updateValues('pf', 'kva', calc.toFixed(4));
      } else if (!pf || pf === '') {
        calc = k > 0 && w > 0 ? w / k : 0;
        setResult({ field: 'pf', value: calc.toFixed(4), unit: '' });
        updateValues('pf', 'pf', calc.toFixed(4));
      } else {
        calc = k * p;
        setResult({ field: 'kw', value: calc.toFixed(4), unit: 'kW' });
        updateValues('pf', 'kw', calc.toFixed(4));
      }
    }
  };

  const handleReset = () => {
    setValues(JSON.parse(JSON.stringify(initialValues)));
    setResult(null);
  };

  const v1 = values['1phase'];
  const v3 = values['3phase'];
  const vPf = values.pf;

  const canCalc1 = countFilled([v1.kva, v1.volts, v1.amps]) === 2;
  const canCalc3 = countFilled([v3.kva, v3.volts, v3.amps]) === 2;
  const canCalcPf = countFilled([vPf.kva, vPf.pf, vPf.kw]) === 2;

  const canCalculate =
    (activeTab === '1phase' && canCalc1) ||
    (activeTab === '3phase' && canCalc3) ||
    (activeTab === 'pf' && canCalcPf);

  const currentTab = TABS.find((t) => t.id === activeTab);

  const content = (
    <div className="max-w-2xl mx-auto">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-green-600 dark:bg-green-700 px-4 py-3 rounded-t-lg -mx-4 -mt-4 mb-0 sm:rounded-t-none sm:mx-0 sm:mt-0 sm:-mx-0 sm:-mt-0 sm:rounded-lg">
        <Link
          to={backPath}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-lg font-semibold text-white">{currentTab?.title}</h1>
        <Link
          to={isMobile ? '/tenant/mobile' : '/tenant/dashboard'}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Home"
        >
          <Home className="h-5 w-5" />
        </Link>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 mb-4">
        Enter any two values to calculate the third
      </p>

      <div className={`space-y-4 ${isMobile ? 'pb-24' : ''}`}>
        {activeTab === '1phase' && (
          <>
            <InputRow
              label="kVA"
              placeholder="kVA"
              value={v1.kva}
              onChange={(val) => updateValues('1phase', 'kva', val)}
            />
            <InputRow
              label="Volts"
              placeholder="L-N Voltage"
              value={v1.volts}
              onChange={(val) => updateValues('1phase', 'volts', val)}
            />
            <InputRow
              label="Amps"
              placeholder="Amperes"
              value={v1.amps}
              onChange={(val) => updateValues('1phase', 'amps', val)}
            />
          </>
        )}

        {activeTab === '3phase' && (
          <>
            <InputRow
              label="kVA"
              placeholder="kVA"
              value={v3.kva}
              onChange={(val) => updateValues('3phase', 'kva', val)}
            />
            <InputRow
              label="Volts"
              placeholder="L-L Voltage"
              value={v3.volts}
              onChange={(val) => updateValues('3phase', 'volts', val)}
            />
            <InputRow
              label="Amps"
              placeholder="Amperes"
              value={v3.amps}
              onChange={(val) => updateValues('3phase', 'amps', val)}
            />
          </>
        )}

        {activeTab === 'pf' && (
          <>
            <InputRow
              label="kVA"
              placeholder="kVA"
              value={vPf.kva}
              onChange={(val) => updateValues('pf', 'kva', val)}
            />
            <InputRow
              label="PF"
              placeholder="Power Factor"
              value={vPf.pf}
              onChange={(val) => updateValues('pf', 'pf', val)}
              infoTooltip="Power Factor: ratio of real power (kW) to apparent power (kVA). Typically 0.8–0.95 for electrical systems. Enter as decimal (e.g. 0.85) or percentage (e.g. 85)."
            />
            <InputRow
              label="kW"
              placeholder="kW"
              value={vPf.kw}
              onChange={(val) => updateValues('pf', 'kw', val)}
            />
          </>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleCalculate}
            disabled={!canCalculate}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Calculator className="h-4 w-4" />
            Calculate
          </Button>
          <Button
            onClick={handleReset}
            variant="destructive"
            className="flex-1 py-3 flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>
      </div>

      {/* Bottom navigation tabs - fixed on mobile, inline on desktop */}
      <nav
        className={`flex border-t border-gray-200 dark:border-gray-700 bg-gray-800 dark:bg-gray-900 ${
          isMobile ? 'fixed bottom-0 left-0 right-0 z-40' : 'mt-6 -mx-6 -mb-6 rounded-b-lg overflow-hidden'
        }`}
        role="tablist"
      >
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => {
                setActiveTab(tab.id);
                setResult(null);
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-blue-600 dark:bg-blue-500 text-white'
                  : 'text-white/80 hover:text-white hover:bg-gray-700 dark:hover:bg-gray-800'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>
    </div>
  );

  if (isMobile) {
    return (
      <div className="px-4 py-6 pb-8 min-h-screen bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
        {content}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            kVA Calculator
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Calculate kVA, voltage, current, power factor, or kW for single-phase and three-phase systems
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
          {content}
        </div>
      </div>
    </Layout>
  );
}
